import {computed, Injectable, signal} from "@angular/core"
import {Subject} from "rxjs"
import {IsDefined} from "@cm/utils/filter"

export type DefaultNode = {id: string}

@Injectable({
    providedIn: "root",
})
export class NodesService<Node extends DefaultNode> {
    private readonly $_nodes = signal<Node[]>([])

    public readonly $nodes = computed(() => this.$_nodes())

    readonly onAdd$ = new Subject<Node[]>()
    readonly onUpdate$ = new Subject<Node[]>()
    readonly onDelete$ = new Subject<Node[]>()

    add = (nodes: Node | Node[]) => {
        if (!Array.isArray(nodes)) {
            nodes = [nodes]
        }
        const newNodes = nodes.filter((node: Node) => !this.$_nodes().some((existingNode) => existingNode.id === node.id))
        this.$_nodes.update((nodes) => [...nodes, ...newNodes])
        this.onAdd$.next(newNodes)
    }

    update = (updatedNodes: Node | Node[]) => {
        if (!Array.isArray(updatedNodes)) {
            updatedNodes = [updatedNodes]
        }
        this.$_nodes.update((nodes) =>
            nodes.map((node) => {
                const update = updatedNodes.find((updatedNode) => updatedNode.id === node.id)
                return update ?? node
            }),
        )
        this.onUpdate$.next(
            this.$_nodes()
                .filter((node) => updatedNodes.find((updatedNode) => updatedNode.id === node.id))
                .filter(IsDefined),
        )
    }

    updateAll = () => {
        this.onUpdate$.next(this.$_nodes())
    }

    remove = (nodeIdsToBeDeleted: string[]) => {
        for (const nodeIdToBeDeleted of nodeIdsToBeDeleted) {
            const groupedNodes = Object.groupBy(this.$_nodes(), (node) => (node.id === nodeIdToBeDeleted ? "deleted" : "remaining"))

            this.$_nodes.set(groupedNodes.remaining ?? [])

            if (groupedNodes.deleted?.length) {
                this.onDelete$.next(groupedNodes.deleted)
            }
        }
    }

    removeAll = () => {
        const allNodes = this.$_nodes()
        this.$_nodes.set([])
        this.onDelete$.next(allNodes)
    }

    get = (id: string) => {
        return this.$_nodes().find((node: Node) => node.id === id)
    }

    synchronize = (nodes: Node[]) => {
        const {updatedNodes, deletedNodes} = Object.groupBy(this.$_nodes(), (existingNode) =>
            nodes.some((newNode) => newNode.id === existingNode.id) ? "updatedNodes" : "deletedNodes",
        )
        this.add(nodes)
        this.remove((deletedNodes ?? []).map((node) => node.id))
        this.onUpdate$.next(updatedNodes ?? [])
    }

    readonly onSelect = new Subject<Node[]>()
    readonly onDeselect = new Subject<Node[]>()
    selectedNodeIds: string[] = []

    get selected() {
        return this.selectedNodeIds.map((nodeId) => this.$_nodes().find((node) => node.id === nodeId)).filter(IsDefined)
    }

    setSelected = (nodeIds: string | string[]) => {
        if (!Array.isArray(nodeIds)) {
            nodeIds = [nodeIds]
        }
        const {selected, stillSelected, deselected} = Object.groupBy(this.$_nodes(), (node) => {
            if (this.selectedNodeIds.includes(node.id)) {
                if (nodeIds.includes(node.id)) {
                    return "stillSelected"
                } else {
                    return "deselected"
                }
            } else {
                if (nodeIds.includes(node.id)) {
                    return "selected"
                } else {
                    return "stillDeselected"
                }
            }
        })
        if (deselected?.length) {
            this.onDeselect.next(deselected)
        }
        if (selected?.length) {
            this.onSelect.next(selected)
        }
        if (deselected?.length || selected?.length) {
            this.selectedNodeIds = [...(stillSelected ?? []), ...(selected ?? [])].map((node) => node.id)
        }
    }

    select = (nodeIds: string | string[]) => {
        if (!Array.isArray(nodeIds)) {
            nodeIds = [nodeIds]
        }
        const newlySelectedNodes = this.$_nodes().filter((node) => !this.selectedNodeIds.includes(node.id) && nodeIds.includes(node.id))
        if (newlySelectedNodes?.length) {
            this.onSelect.next(newlySelectedNodes)
            this.selectedNodeIds = (newlySelectedNodes ?? []).map((node) => node.id)
        }
    }

    deselect = (nodeIds: string | string[]) => {
        if (!Array.isArray(nodeIds)) {
            nodeIds = [nodeIds]
        }
        const newlyDeselectedNodes = this.$_nodes().filter((node) => this.selectedNodeIds.includes(node.id) && nodeIds.includes(node.id))
        if (newlyDeselectedNodes?.length) {
            this.onDeselect.next(newlyDeselectedNodes)
            this.selectedNodeIds = this.selectedNodeIds.filter((nodeId) => !nodeIds.includes(nodeId))
        }
    }

    deselectAll = () => {
        this.deselect(this.selectedNodeIds)
    }
}
